/**
 * Creative detail modal (Creations `?creative=`, Decisions, chat approval cards, Studio results).
 *
 * Tools:
 *  - creatives.get {brandId, creativeId}
 *      → {creative (row), assets (every version incl. superseded, with qaReport / editInstruction),
 *         approvals, feedback, lineage:{ancestors, children}, batch, card}   (normalizeCreativeDetail)
 *  - ads.structure {brandId, includeAds:true} → {campaigns:[{platform, adSets:[{ads:[{id, name, status,
 *      effectiveStatus, creativeId, launchedAt, …}]}]}]} — filtered to this creative for the Ads tab
 *      (creatives.get carries no ads); only fetched once the creative has been published.
 *  - creatives.approve / creatives.reject  (DecisionActions: pending_approval → ApprovalActions;
 *      qa_failed → "Approve anyway" with overrideQa:true, or reject with reasons)
 *  - creatives.regenerate_asset, creatives.iterate, ads.publish, ads.activate (see CreativeActionDialogs)
 *  - creatives.feedback {brandId, creativeId, kind:"comment", note}
 *
 * Internal navigation (lineage parent/children) swaps the shown creative without touching the caller's
 * state; closing always calls the caller's onClose.
 */
import {
  ArrowUpRight,
  Check,
  ChevronRight,
  Clock3,
  ExternalLink,
  GitBranch,
  Layers,
  Lock,
  MessageSquare,
  MoreHorizontal,
  Quote,
  Rocket,
  Send,
  SquarePen,
  ThumbsDown,
  ThumbsUp,
  Wand2,
  X,
  Zap,
} from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { useTool, useToolMutation } from "@/api/hooks";
import { creativeSource, REJECTION_LABEL, type AdRow, type Approval, type Creative, type CreativeAsset, type Feedback, type QaIssue, type RejectionReason } from "@/api/types";
import { cn } from "@/lib/cn";
import { dateTime, formatMetric, humanize, metricLabel, relative } from "@/lib/format";
import { brandPath } from "@/state/brand";
import { Pill } from "../ui/Badge";
import { Button, IconButton } from "../ui/Button";
import { EmptyState, ErrorState, Skeleton } from "../ui/Feedback";
import { Segmented, Tabs, Textarea } from "../ui/Form";
import { LogoTile, platformLabel } from "../ui/Logos";
import { Dropdown, MenuItem, Modal, Tooltip } from "../ui/Overlay";
import { ActivateDialog, IterateDialog, PublishDialog, RegenerateDialog } from "./CreativeActionDialogs";
import { AspectPair, CreativeMedia, CreativeStatusPill, VerdictPill } from "./CreativeBits";
import { DecisionActions } from "./DecisionActions";
import { SOURCE_META } from "./CreativeCard";
import { useTopmostClose } from "./hooks";
import { creativePreview, normalizeCreativeDetail, type CreativeDetail } from "./normalize";
import { AssetVersionsViewer } from "./AssetVersionsViewer";
import { QaReportView } from "./QaReportView";
import { readShotPlan, ScriptReviewPanel } from "./ScriptReviewPanel";
import { safeMediaHref } from "@/lib/safe-url";
import { editorPath } from "@/pages/editor/paths";

export interface CreativeDetailModalProps {
  brandId: string;
  creativeId: string | null;
  open: boolean;
  onClose: () => void;
}

type Tab = "details" | "activity" | "lineage" | "ads";
type Sub = { kind: "regenerate"; assetId?: string; issue?: QaIssue | null } | { kind: "iterate" } | { kind: "publish" } | { kind: "activate"; ad: AdRow } | null;

const NEEDS_DECISION = new Set(["pending_approval", "qa_failed"]);
/** In an ad account — assets can't be re-rendered (server refuses); iterate instead. */
const IN_ACCOUNT = new Set(["published", "live", "killed", "graveyard", "dead"]);

/** Ads for this creative from the synced account structure (platform comes from the campaign). */
function useCreativeAds(brandId: string, creativeId: string, enabled: boolean): { ads: AdRow[]; loading: boolean } {
  const q = useTool<unknown>("ads.structure", enabled ? { brandId, includeAds: true } : null);
  const ads = useMemo(() => {
    const out: AdRow[] = [];
    const root = (q.data ?? {}) as { campaigns?: unknown };
    for (const camp of Array.isArray(root.campaigns) ? (root.campaigns as Record<string, unknown>[]) : []) {
      for (const set of Array.isArray(camp.adSets) ? (camp.adSets as Record<string, unknown>[]) : []) {
        for (const ad of Array.isArray(set.ads) ? (set.ads as Record<string, unknown>[]) : []) {
          if (ad.creativeId !== creativeId || typeof ad.id !== "string") continue;
          out.push({
            id: ad.id,
            externalId: typeof ad.externalId === "string" ? ad.externalId : undefined,
            name: typeof ad.name === "string" ? ad.name : "Ad",
            platform: typeof camp.platform === "string" ? camp.platform : undefined,
            status: typeof ad.status === "string" ? ad.status : undefined,
            effectiveStatus: typeof ad.effectiveStatus === "string" ? ad.effectiveStatus : null,
            creativeId,
            campaignName: typeof camp.name === "string" ? camp.name : undefined,
            adSetName: typeof set.name === "string" ? set.name : undefined,
            launchedAt: typeof ad.launchedAt === "string" ? ad.launchedAt : null,
          });
        }
      }
    }
    return out;
  }, [q.data, creativeId]);
  return { ads, loading: q.isLoading };
}

export function CreativeDetailModal({ brandId, creativeId, open, onClose }: CreativeDetailModalProps) {
  const [currentId, setCurrentId] = useState<string | null>(creativeId);
  const [tab, setTab] = useState<Tab>("details");
  const [sub, setSub] = useState<Sub>(null);
  useEffect(() => {
    setCurrentId(creativeId);
    setTab("details");
    setSub(null);
  }, [creativeId, open]);
  const { marker, close } = useTopmostClose(onClose);
  /** After an edit/regenerate request, keep polling for the new version for a while. */
  const [pollUntil, setPollUntil] = useState(0);
  const q = useTool<unknown>("creatives.get", open && currentId ? { brandId, creativeId: currentId } : null, {
    keepPrevious: false,
    refetchInterval: (d) => {
      const n = normalizeCreativeDetail(d);
      const busy = !!n && (n.creative.status === "generating" || (n.creative.assets ?? []).some((a) => a.status === "pending" || a.status === "generating"));
      const feedbackBusy = n?.feedback.some((f) => f.meta?.autoFix && f.meta.feedbackJobStatus && ["queued", "running", "retry"].includes(f.meta.feedbackJobStatus));
      return busy || feedbackBusy || Date.now() < pollUntil ? 4000 : false;
    },
  });
  const detail = useMemo(() => normalizeCreativeDetail(q.data), [q.data]);
  const stale = !!detail && detail.creative.id !== currentId;

  return (
    <Modal open={open} onClose={close} size="full" hideClose className={cn(marker, "sm:h-[90vh]")} bodyClassName="flex min-h-0 flex-col overflow-hidden p-0">
      {q.isLoading || (!detail && q.isFetching) ? (
        <DetailSkeleton onClose={close} />
      ) : q.error && !detail ? (
        <div className="flex min-h-[50vh] flex-col">
          <div className="flex justify-end p-3">
            <IconButton label="Close" onClick={close}>
              <X className="size-4.5" />
            </IconButton>
          </div>
          <ErrorState error={q.error} onRetry={() => void q.refetch()} />
        </div>
      ) : !detail ? (
        <div className="flex min-h-[50vh] flex-col">
          <div className="flex justify-end p-3">
            <IconButton label="Close" onClick={close}>
              <X className="size-4.5" />
            </IconButton>
          </div>
          <EmptyState title="Creative not found" body="It may have been deleted, or it belongs to another brand." />
        </div>
      ) : (
        <DetailBody
          brandId={brandId}
          detail={detail}
          stale={stale}
          tab={tab}
          setTab={setTab}
          onNavigate={(id) => {
            setCurrentId(id);
            setTab("details");
          }}
          onSub={setSub}
          onClose={close}
          onEditRequested={() => setPollUntil(Date.now() + 3 * 60_000)}
        />
      )}
      {detail && (
        <>
          <RegenerateDialog
            open={sub?.kind === "regenerate"}
            onClose={() => {
              setSub(null);
              setPollUntil(Date.now() + 3 * 60_000);
            }}
            brandId={brandId}
            creative={detail.creative}
            initialAssetId={sub?.kind === "regenerate" ? sub.assetId : null}
            initialIssue={sub?.kind === "regenerate" ? sub.issue : null}
          />
          <IterateDialog open={sub?.kind === "iterate"} onClose={() => setSub(null)} brandId={brandId} creative={detail.creative} />
          <PublishDialog open={sub?.kind === "publish"} onClose={() => setSub(null)} brandId={brandId} creative={detail.creative} />
          <ActivateDialog open={sub?.kind === "activate"} onClose={() => setSub(null)} brandId={brandId} ad={sub?.kind === "activate" ? sub.ad : null} />
        </>
      )}
    </Modal>
  );
}

// ─── body ──────────────────────────────────────────────────────────────────────

function DetailBody({
  brandId,
  detail,
  stale,
  tab,
  setTab,
  onNavigate,
  onSub,
  onClose,
  onEditRequested,
}: {
  brandId: string;
  detail: CreativeDetail;
  stale: boolean;
  tab: Tab;
  setTab: (t: Tab) => void;
  onNavigate: (id: string) => void;
  onSub: (s: Sub) => void;
  onClose: () => void;
  onEditRequested: () => void;
}) {
  const [mediaView, setMediaView] = useState<"pair" | "versions">("pair");
  const c = detail.creative;
  const src = SOURCE_META[creativeSource(c)];
  const needsDecision = NEEDS_DECISION.has(String(c.status));
  const canPublish = c.status === "approved";
  const imported = creativeSource(c) === "imported";
  const inAccount = IN_ACCOUNT.has(String(c.status));
  const fixBlocked = imported || inAccount || c.status === "generating" || c.status === "script_review";
  const editBlockedReason =
    imported || inAccount ? "In an ad account — iterate to make a new version" : c.status === "generating" ? "Still generating…" : c.status === "script_review" ? "Approve the script first" : null;
  const shotPlan = readShotPlan((c as unknown as { shotPlan?: unknown }).shotPlan);
  const aspectCount = new Set((c.assets ?? []).map((a) => a.aspect)).size;
  const versionCount = (c.assets ?? []).length;
  const fetched = useCreativeAds(brandId, c.id, inAccount || imported);
  const ads = detail.ads.length ? detail.ads : fetched.ads;
  const activity = detail.approvals.length + detail.feedback.length;
  const correction = detail.feedback.find((f) => f.meta?.autoFix);
  const navigate = useNavigate();
  return (
    <div className={cn("flex min-h-0 flex-1 flex-col transition-opacity", stale && "opacity-60")}>
      {c.status === "rejected" && correction && <div className="flex flex-wrap items-center gap-2 border-b border-line bg-primary-soft px-5 py-3 text-[12.5px]">
        <span className="mr-auto">{correction.meta?.correctionCreativeIds?.length ? "A corrected version has been created for review." : ["failed", "cancelled", "dead"].includes(correction.meta?.feedbackJobStatus ?? "") ? "The correction could not finish. Your rejection feedback is saved." : "Mimic is preparing a corrected version from your feedback."}</span>
        {correction.meta?.correctionCreativeIds?.map((id) => <Button key={id} size="xs" variant="secondary" onClick={() => onNavigate(id)}>View corrected version</Button>)}
      </div>}
      {/* header */}
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-5 py-4">
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-[18px] font-semibold tracking-[-0.01em]">{c.name}</h2>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[12px] text-muted">
            <CreativeStatusPill status={c.status} />
            <Pill tone="neutral">{c.kind === "video" ? "Video" : "Image"}</Pill>
            {src && (
              <Pill tone="muted" icon={src.icon}>
                {src.label}
              </Pill>
            )}
            {typeof c.generation === "number" && <Pill tone="primary">Gen {c.generation}</Pill>}
            {detail.verdicts[0] && <VerdictPill verdict={detail.verdicts[0].verdict} />}
            <span className="ml-1 inline-flex items-center gap-1">
              <Clock3 className="size-3.5" strokeWidth={1.75} />
              {relative(c.createdAt)}
            </span>
            {detail.batch && (
              <Link to={brandPath(brandId, `studio?batch=${encodeURIComponent(detail.batch.id)}`)} onClick={onClose} className="inline-flex items-center gap-0.5 text-link hover:text-lavender">
                {detail.batch.title || "View batch"} <ArrowUpRight className="size-3" />
              </Link>
            )}
          </div>
          {c.statusReason && <p className="mt-1.5 text-[12.5px] text-muted">{c.statusReason}</p>}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {c.kind !== "video" && (c.assets ?? []).some((a) => a.mediaType === "image" && a.url) && (
            <Tooltip content={editBlockedReason ? `${editBlockedReason} — you can still preview and download` : "Edit it by talking to the AI, with filters and adjustments"}>
              <Button size="sm" variant="secondary" icon={<SquarePen className="size-3.5" strokeWidth={1.75} />} onClick={() => navigate(editorPath(brandId, c.id))}>
                <span className="hidden sm:inline">Open in editor</span>
                <span className="sm:hidden">Editor</span>
              </Button>
            </Tooltip>
          )}
          <Tooltip
            content={
              imported || inAccount
                ? "It's in an ad account — iterate to make a new version instead"
                : c.status === "generating"
                  ? "Still generating — wait for the batch to finish"
                  : "Regenerate only the flawed element"
            }
          >
            <Button
              size="sm"
              variant="secondary"
              icon={<Wand2 className="size-3.5" strokeWidth={1.75} />}
              disabled={fixBlocked || !(c.assets ?? []).length}
              onClick={() => onSub({ kind: "regenerate" })}
            >
              <span className="hidden sm:inline">Regenerate element</span>
              <span className="sm:hidden">Fix</span>
            </Button>
          </Tooltip>
          <Button size="sm" variant="secondary" icon={<Layers className="size-3.5" strokeWidth={1.75} />} onClick={() => onSub({ kind: "iterate" })}>
            Iterate
          </Button>
          {canPublish && (
            <Button size="sm" variant="primary" icon={<Rocket className="size-3.5" strokeWidth={1.75} />} onClick={() => onSub({ kind: "publish" })}>
              Publish
            </Button>
          )}
          <Dropdown
            align="end"
            trigger={
              <IconButton label="More actions" variant="outline">
                <MoreHorizontal className="size-4" />
              </IconButton>
            }
          >
            {(closeMenu) => (
              <>
                <MenuItem
                  icon={<Wand2 className="size-3.5" />}
                  onClick={() => {
                    closeMenu();
                    onClose();
                    navigate(`/chat?prompt=${encodeURIComponent(`Make 3 variations of creative "${c.name}" (${c.id}) keeping the same hook and angle.`)}`);
                  }}
                >
                  Ask Mimic for variations
                </MenuItem>
                <MenuItem
                  icon={<Check className="size-3.5" />}
                  onClick={() => {
                    closeMenu();
                    void navigator.clipboard?.writeText(c.id);
                  }}
                >
                  Copy creative ID
                </MenuItem>
              </>
            )}
          </Dropdown>
          <IconButton label="Close" onClick={onClose}>
            <X className="size-4.5" />
          </IconButton>
        </div>
      </header>

      <div className="grid min-h-0 flex-1 overflow-y-auto scrollbar-thin lg:grid-cols-[minmax(0,1.12fr)_minmax(360px,0.88fr)] lg:overflow-hidden">
        {/* left: media, decision, QA */}
        <section className="flex min-h-0 flex-col gap-4 p-5 lg:overflow-y-auto lg:scrollbar-thin">
          {c.status === "script_review" && shotPlan && <ScriptReviewPanel brandId={brandId} creativeId={c.id} plan={shotPlan} />}
          {(c.assets ?? []).length > 0 || c.status !== "script_review" ? (
            <div className="flex flex-col gap-2">
              {(c.assets ?? []).length > 0 && (
                <div className="flex items-center justify-between gap-2">
                  <Segmented
                    size="sm"
                    value={mediaView}
                    onChange={setMediaView}
                    options={[
                      { value: "pair", label: "Both aspects" },
                      { value: "versions", label: versionCount > aspectCount ? `Versions & edits · ${versionCount}` : "Versions & edits" },
                    ]}
                  />
                  {mediaView === "versions" && <span className="text-[11.5px] text-subtle">Describe an edit to make the next version</span>}
                </div>
              )}
              {mediaView === "versions" && (c.assets ?? []).length > 0 ? (
                <AssetVersionsViewer brandId={brandId} creative={c} blockedReason={editBlockedReason} onEditRequested={onEditRequested} />
              ) : (
                <div className="rounded-card border border-line bg-[#141416] p-4">
                  <AspectPair creative={c} height={340} className="justify-center" />
                </div>
              )}
            </div>
          ) : null}
          {needsDecision && (
            <div className="flex flex-col gap-3 rounded-card border border-amber/30 bg-amber-soft/40 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-[13px]">
                <div className="font-semibold text-fg">{c.status === "qa_failed" ? "QA flagged this creative" : "Waiting for your decision"}</div>
                <p className="mt-0.5 text-muted">Nothing touches an ad account without human approval. Rejections are logged as training data for the next batch.</p>
              </div>
              <DecisionActions brandId={brandId} creativeId={c.id} status={String(c.status)} className="shrink-0" />
            </div>
          )}
          {(c.assets ?? []).length > 0 && (
            <QaReportView assets={c.assets ?? []} onFix={fixBlocked ? undefined : (asset: CreativeAsset, issue) => onSub({ kind: "regenerate", assetId: asset.id, issue })} />
          )}
        </section>

        {/* right: tabs */}
        <aside className="flex min-h-0 flex-col border-line lg:border-l">
          <Tabs<Tab>
            className="shrink-0 px-5 pt-3"
            value={tab}
            onChange={setTab}
            options={[
              { value: "details", label: "Details" },
              { value: "activity", label: "Activity", count: activity || undefined },
              { value: "lineage", label: "Lineage", count: detail.children.length || undefined },
              { value: "ads", label: "Ads", count: ads.length || undefined },
            ]}
          />
          <div className="min-h-0 flex-1 p-5 lg:overflow-y-auto lg:scrollbar-thin">
            {tab === "details" && <DetailsTab brandId={brandId} detail={detail} />}
            {tab === "activity" && <ActivityTab brandId={brandId} detail={detail} />}
            {tab === "lineage" && <LineageTab detail={detail} onNavigate={onNavigate} onIterate={() => onSub({ kind: "iterate" })} />}
            {tab === "ads" && <AdsTab ads={ads} loading={fetched.loading} canPublish={canPublish} onPublish={() => onSub({ kind: "publish" })} onActivate={(ad) => onSub({ kind: "activate", ad })} />}
          </div>
        </aside>
      </div>
    </div>
  );
}

// ─── tabs ──────────────────────────────────────────────────────────────────────

function Section({ title, children, className, right }: { title: ReactNode; children: ReactNode; className?: string; right?: ReactNode }) {
  return (
    <section className={cn("flex flex-col gap-2", className)}>
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-subtle">{title}</h3>
        {right}
      </div>
      {children}
    </section>
  );
}

function KV({ label, children, mono }: { label: string; children: ReactNode; mono?: boolean }) {
  return (
    <div className="grid grid-cols-[120px_minmax(0,1fr)] gap-3 py-1.5 text-[13px]">
      <dt className="text-muted">{label}</dt>
      <dd className={cn("min-w-0 break-words text-fg", mono && "font-mono text-[12px]")}>{children ?? <span className="text-subtle">—</span>}</dd>
    </div>
  );
}

function DetailsTab({ brandId, detail }: { brandId: string; detail: CreativeDetail }) {
  const c = detail.creative;
  const raw = c as unknown as Record<string, unknown>;
  const quote = (typeof raw.hookQuote === "string" && raw.hookQuote) || (typeof raw.sourceQuote === "string" && raw.sourceQuote) || null;
  const ref = detail.reference;
  const perf = c.perf ? Object.entries(c.perf).filter(([, v]) => typeof v === "number") : [];
  return (
    <div className="flex flex-col gap-6">
      <Section title="Hook">
        <div className="relative rounded-xl border border-line bg-surface-2 p-4">
          <Quote className="absolute right-3 top-3 size-5 text-elevated-2" strokeWidth={1.5} />
          <p className="pr-6 text-[16px] font-semibold leading-snug text-balance">{c.hook ?? "—"}</p>
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            {c.emotionalTrigger && (
              <Pill tone="primary" size="xs">
                {humanize(String(c.emotionalTrigger))}
              </Pill>
            )}
            {c.awarenessStage && (
              <Pill tone="info" size="xs">
                {humanize(String(c.awarenessStage))}
              </Pill>
            )}
            {c.hookSource && (
              <Pill tone="muted" size="xs">
                Source · {humanize(c.hookSource)}
              </Pill>
            )}
          </div>
          {quote && (
            <blockquote className="mt-3 border-l-2 border-amber/60 pl-3 text-[12.5px] italic leading-relaxed text-muted">
              “{quote}”<span className="not-italic text-subtle"> — verbatim{c.hookSource === "app_review" ? " App Store review" : ""}</span>
            </blockquote>
          )}
        </div>
      </Section>

      <Section title="Strategy">
        <dl className="divide-y divide-line/60">
          <KV label="Concept">{c.concept}</KV>
          <KV label="Angle">{c.angle}</KV>
          <KV label="Emotional trigger">{c.emotionalTrigger ? humanize(String(c.emotionalTrigger)) : null}</KV>
          <KV label="Awareness stage">{c.awarenessStage ? humanize(String(c.awarenessStage)) : null}</KV>
          <KV label="Format">{c.format ? humanize(String(c.format)) : null}</KV>
          {typeof raw.layout === "string" && <KV label="Layout">{humanize(raw.layout)}</KV>}
          <KV label="Source reference">
            {ref ? (
              <a href={safeMediaHref(ref.url)} target="_blank" rel="noreferrer" className="group flex items-center gap-2.5 rounded-lg border border-line p-1.5 pr-2.5 hover:border-line-strong">
                <span className="size-10 shrink-0 overflow-hidden rounded-md">
                  <CreativeMedia url={ref.thumbnailUrl ?? ref.mediaUrl} mediaType={ref.mediaType === "video" ? "video" : "image"} className="size-full" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[12.5px] font-medium">{ref.pageName ?? ref.creator ?? humanize(ref.source)}</span>
                  <span className="block text-[11.5px] text-muted">{ref.daysRunning ? `${ref.daysRunning} days running` : humanize(ref.source)}</span>
                </span>
                <ExternalLink className="size-3.5 text-muted group-hover:text-fg" />
              </a>
            ) : c.sourceReferenceId ? (
              <Link to={brandPath(brandId, `inspiration?reference=${encodeURIComponent(c.sourceReferenceId)}`)} className="inline-flex items-center gap-1 text-link hover:text-lavender">
                View reference <ArrowUpRight className="size-3" />
              </Link>
            ) : null}
          </KV>
        </dl>
      </Section>

      <Section title="Copy">
        <dl className="divide-y divide-line/60">
          <KV label="Headline">{c.headline}</KV>
          <KV label="Primary text">{c.primaryText ? <span className="whitespace-pre-wrap">{c.primaryText}</span> : null}</KV>
          <KV label="CTA">{c.cta ? humanize(c.cta) : null}</KV>
          <KV label="Destination">
            {c.destinationKey ? (
              <span className="inline-flex items-center gap-1.5">
                <code className="rounded-md bg-surface-2 px-1.5 py-0.5 font-mono text-[11.5px]">{c.destinationKey}</code>
                <span className="text-[11.5px] text-subtle">URL resolved from brand config</span>
              </span>
            ) : null}
          </KV>
        </dl>
        {(c.overlayText ?? []).length > 0 && (
          <div className="mt-1 rounded-xl border border-line bg-surface-2 p-3">
            <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-medium text-subtle">
              <Lock className="size-3" /> Exact on-image text (QA checks it verbatim)
            </div>
            <ol className="flex flex-col gap-1 text-[13px]">
              {(c.overlayText ?? []).map((t, i) => (
                <li key={i} className="flex gap-2">
                  <span className="w-4 shrink-0 text-right text-subtle tnum">{i + 1}</span>
                  <span>{t}</span>
                </li>
              ))}
            </ol>
          </div>
        )}
      </Section>

      {typeof raw.visualBrief === "string" && raw.visualBrief && (
        <Section title="Visual brief">
          <p className="text-[13px] leading-relaxed text-fg/85">{raw.visualBrief}</p>
        </Section>
      )}

      {perf.length > 0 && (
        <Section title="Performance (lifetime)">
          <div className="grid grid-cols-3 gap-2">
            {perf.slice(0, 9).map(([k, v]) => (
              <div key={k} className="rounded-lg border border-line bg-surface-2 px-3 py-2">
                <div className="text-[11px] text-muted">{metricLabel(k)}</div>
                <div className="text-[14px] font-semibold tnum">{formatMetric(k, v as number, "USD", { compact: true })}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {detail.verdicts.length > 0 && (
        <Section title="Verdicts">
          <ul className="flex flex-col gap-1.5">
            {detail.verdicts.slice(0, 5).map((v, i) => (
              <li key={i} className="flex items-start gap-2 text-[12.5px]">
                <VerdictPill verdict={v.verdict} />
                <span className="min-w-0 flex-1 text-muted">{v.reasons.join(" · ") || "—"}</span>
                {v.createdAt && <span className="shrink-0 text-subtle">{relative(v.createdAt)}</span>}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {(c.tags ?? []).length > 0 && (
        <Section title="Tags">
          <div className="flex flex-wrap gap-1.5">
            {(c.tags ?? []).map((t) => (
              <Pill key={t} tone="muted" size="xs">
                {t}
              </Pill>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

interface TimelineItem {
  id: string;
  at: string | null;
  icon: ReactNode;
  tone: string;
  title: ReactNode;
  who?: string | null;
  reasons: RejectionReason[];
  note?: string | null;
}

function timeline(approvals: Approval[], feedback: Feedback[]): TimelineItem[] {
  const items: TimelineItem[] = [];
  for (const a of approvals) {
    if (a.requestedAt) items.push({ id: `${a.id}-req`, at: a.requestedAt, icon: <Send className="size-3.5" />, tone: "bg-elevated text-muted", title: "Approval requested", reasons: [] });
    if (a.decision === "approved" || a.decision === "rejected") {
      items.push({
        id: a.id,
        at: a.decidedAt ?? a.requestedAt ?? null,
        icon: a.decision === "approved" ? <ThumbsUp className="size-3.5" /> : <ThumbsDown className="size-3.5" />,
        tone: a.decision === "approved" ? "bg-success-soft text-success" : "bg-danger-soft text-danger",
        title: a.decision === "approved" ? "Approved" : "Rejected",
        who: a.decidedBy,
        reasons: a.reasons ?? [],
        note: a.note,
      });
    }
  }
  for (const f of feedback) {
    const kind = String(f.kind ?? "comment");
    // Rejections are mirrored in feedback; skip exact duplicates of an approval decision.
    if (kind === "rejection" && items.some((i) => i.title === "Rejected" && (i.note ?? null) === (f.note ?? null) && JSON.stringify(i.reasons) === JSON.stringify(f.reasons ?? []))) continue;
    if (kind === "approval" && items.some((i) => i.title === "Approved")) continue;
    items.push({
      id: f.id,
      at: f.createdAt ?? null,
      icon:
        kind === "comment" ? (
          <MessageSquare className="size-3.5" />
        ) : kind === "rejection" ? (
          <ThumbsDown className="size-3.5" />
        ) : kind === "approval" ? (
          <ThumbsUp className="size-3.5" />
        ) : kind === "edit_request" ? (
          <Wand2 className="size-3.5" />
        ) : (
          <Zap className="size-3.5" />
        ),
      tone: kind === "rejection" ? "bg-danger-soft text-danger" : kind === "approval" ? "bg-success-soft text-success" : "bg-primary-soft text-lavender",
      title: kind === "comment" ? "Note" : kind === "edit_request" ? "Fix requested" : kind === "rating" && typeof f.rating === "number" ? `Rated ${f.rating}/5` : humanize(kind),
      who: f.userId,
      reasons: f.reasons ?? [],
      note: f.note,
    });
  }
  return items.sort((a, b) => (b.at ?? "").localeCompare(a.at ?? ""));
}

function ActivityTab({ brandId, detail }: { brandId: string; detail: CreativeDetail }) {
  const [note, setNote] = useState("");
  const fb = useToolMutation("creatives.feedback", { success: "Note saved — it feeds the learning loop", onSuccess: () => setNote("") });
  const items = timeline(detail.approvals, detail.feedback);
  return (
    <div className="flex flex-col gap-5">
      <div className="rounded-xl border border-line bg-surface-2 p-3">
        <Textarea
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Leave feedback for Mimic — e.g. “love the testimonial layout, use it more”"
          className="border-0 bg-transparent px-0 focus:ring-0"
          aria-label="Feedback note"
        />
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="text-[11.5px] text-subtle">Human feedback is training data for the next batch.</span>
          <Button
            size="xs"
            variant="primary"
            disabled={!note.trim()}
            loading={fb.isPending}
            icon={<Send className="size-3" />}
            onClick={() => fb.mutate({ brandId, creativeId: detail.creative.id, kind: "comment", note: note.trim() })}
          >
            Add note
          </Button>
        </div>
      </div>
      {items.length === 0 ? (
        <EmptyState compact icon={<MessageSquare className="size-5" strokeWidth={1.75} />} title="No activity yet" body="Approvals, rejections (with reasons) and notes show up here." />
      ) : (
        <ol className="relative flex flex-col gap-4 pl-1">
          <span className="absolute bottom-2 left-[15px] top-2 w-px bg-line" aria-hidden />
          {items.map((it) => (
            <li key={it.id} className="relative flex gap-3">
              <span className={cn("relative z-10 grid size-[30px] shrink-0 place-items-center rounded-full ring-4 ring-[#1a1a1c]", it.tone)}>{it.icon}</span>
              <div className="min-w-0 flex-1 pt-1">
                <div className="flex flex-wrap items-baseline gap-x-2 text-[13px]">
                  <span className="font-semibold">{it.title}</span>
                  {it.who && <span className="text-muted">by {it.who}</span>}
                  <span className="ml-auto text-[11.5px] text-subtle" title={it.at ? dateTime(it.at) : undefined}>
                    {it.at ? relative(it.at) : ""}
                  </span>
                </div>
                {it.reasons.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {it.reasons.map((r) => (
                      <Pill key={r} tone="danger" size="xs">
                        {REJECTION_LABEL[r] ?? humanize(r)}
                      </Pill>
                    ))}
                  </div>
                )}
                {it.note && <p className="mt-1.5 whitespace-pre-wrap rounded-lg bg-surface-2 px-3 py-2 text-[12.5px] leading-relaxed text-fg/90">{it.note}</p>}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function LineageCard({ c, label, current, onClick }: { c: Creative; label: string; current?: boolean; onClick?: () => void }) {
  const p = creativePreview(c);
  const body = (
    <>
      <span className="size-14 shrink-0 overflow-hidden rounded-lg border border-line">
        <CreativeMedia url={p.url} poster={p.poster} mediaType={p.mediaType} className="size-full" />
      </span>
      <span className="min-w-0 flex-1 text-left">
        <span className="flex items-center gap-1.5 text-[11px] font-medium text-subtle">
          {label}
          {typeof c.generation === "number" && <span>· Gen {c.generation}</span>}
        </span>
        <span className="block truncate text-[13px] font-medium text-fg">{c.name}</span>
        {c.hook && <span className="block truncate text-[12px] text-muted">“{c.hook}”</span>}
      </span>
      <CreativeStatusPill status={c.status} size="xs" />
      {onClick && <ChevronRight className="size-4 shrink-0 text-subtle" />}
    </>
  );
  if (!onClick) return <div className={cn("flex items-center gap-3 rounded-xl border p-2.5", current ? "border-primary/70 bg-primary-soft" : "border-line")}>{body}</div>;
  return (
    <button type="button" onClick={onClick} className="flex w-full items-center gap-3 rounded-xl border border-line p-2.5 transition-colors hover:border-line-strong hover:bg-hover">
      {body}
    </button>
  );
}

function LineageTab({ detail, onNavigate, onIterate }: { detail: CreativeDetail; onNavigate: (id: string) => void; onIterate: () => void }) {
  const { ancestors, children, creative } = detail;
  const chain = [...ancestors].reverse();
  return (
    <div className="flex flex-col gap-2">
      {chain.length === 0 && <p className="px-1 text-[12px] text-subtle">Original — no parent.</p>}
      {chain.map((a, i) => (
        <div key={a.id} className="flex flex-col gap-2">
          <LineageCard c={a} label={i === chain.length - 1 ? "Parent" : i === 0 ? "Original" : "Ancestor"} onClick={() => onNavigate(a.id)} />
          <div className="ml-7 h-4 w-px bg-line-strong" aria-hidden />
        </div>
      ))}
      <LineageCard c={creative} label="This creative" current />
      {children.length > 0 && (
        <>
          <div className="ml-7 h-4 w-px bg-line-strong" aria-hidden />
          <div className="ml-7 flex flex-col gap-2 border-l border-line-strong pl-4">
            {children.map((ch) => (
              <LineageCard key={ch.id} c={ch} label="Iteration" onClick={() => onNavigate(ch.id)} />
            ))}
          </div>
        </>
      )}
      <div className="mt-4 rounded-xl border border-dashed border-line-strong p-4 text-center">
        <GitBranch className="mx-auto mb-2 size-5 text-muted" strokeWidth={1.75} />
        <p className="text-[12.5px] text-muted">When a winner fatigues, rebuild the bench with the SAME hook and angle.</p>
        <Button size="sm" variant="secondary" className="mt-3" icon={<Layers className="size-3.5" />} onClick={onIterate}>
          Iterate this creative
        </Button>
      </div>
    </div>
  );
}

const ACTIVE_STATES = new Set(["active", "enabled", "running"]);

function AdsTab({ ads, loading, canPublish, onPublish, onActivate }: { ads: AdRow[]; loading: boolean; canPublish: boolean; onPublish: () => void; onActivate: (ad: AdRow) => void }) {
  if (loading)
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-16" />
        <Skeleton className="h-16" />
      </div>
    );
  if (ads.length === 0)
    return (
      <EmptyState
        compact
        icon={<Rocket className="size-5" strokeWidth={1.75} />}
        title="Not published yet"
        body={canPublish ? "It's approved. Publish it paused to any connected ad account." : "Creatives need human approval before they can be published."}
        action={
          canPublish ? (
            <Button size="sm" variant="primary" icon={<Rocket className="size-3.5" />} onClick={onPublish}>
              Publish
            </Button>
          ) : undefined
        }
      />
    );
  return (
    <ul className="flex flex-col gap-2">
      {ads.map((ad) => {
        const status = String(ad.status ?? "").toLowerCase();
        const eff = ad.effectiveStatus ? String(ad.effectiveStatus).toLowerCase() : null;
        const active = ACTIVE_STATES.has(status);
        const paused = status === "paused" || status === "";
        return (
          <li key={ad.id} className="flex items-center gap-3 rounded-xl border border-line bg-surface-2 p-3">
            <LogoTile platform={String(ad.platform ?? "")} />
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13px] font-medium">{ad.name}</div>
              <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11.5px] text-muted">
                <Pill tone={active ? "success" : paused ? "neutral" : "warning"} size="xs" dot>
                  {status ? humanize(status) : "Paused"}
                </Pill>
                {eff && eff !== status && <span>effective: {humanize(eff)}</span>}
                <span>· {platformLabel(ad.platform)}</span>
                {ad.launchedAt && <span>· launched {relative(ad.launchedAt)}</span>}
              </div>
            </div>
            {paused && (
              <Tooltip content="Human-only: starts spend">
                <Button size="xs" variant="secondary" icon={<Zap className="size-3" />} onClick={() => onActivate(ad)}>
                  Activate
                </Button>
              </Tooltip>
            )}
          </li>
        );
      })}
      {canPublish && (
        <Button size="sm" variant="ghost" className="self-start" icon={<Rocket className="size-3.5" />} onClick={onPublish}>
          Publish to another platform
        </Button>
      )}
    </ul>
  );
}

function DetailSkeleton({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex min-h-0 flex-1 flex-col" aria-busy="true" aria-label="Loading creative">
      <div className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-5 w-64" />
          <div className="flex gap-1.5">
            <Skeleton className="h-6 w-28 rounded-full" />
            <Skeleton className="h-6 w-16 rounded-full" />
            <Skeleton className="h-6 w-20 rounded-full" />
          </div>
        </div>
        <IconButton label="Close" onClick={onClose}>
          <X className="size-4.5" />
        </IconButton>
      </div>
      <div className="grid min-h-0 flex-1 gap-5 p-5 lg:grid-cols-[minmax(0,1.12fr)_minmax(360px,0.88fr)]">
        <div className="flex flex-col gap-4">
          <div className="flex items-end justify-center gap-3 rounded-card border border-line p-4">
            <Skeleton className="size-[300px] rounded-xl" />
            <Skeleton className="h-[300px] w-[169px] rounded-xl" />
          </div>
          <Skeleton className="h-40" />
        </div>
        <div className="flex flex-col gap-3">
          <Skeleton className="h-8 w-60" />
          <Skeleton className="h-28" />
          <Skeleton className="h-48" />
        </div>
      </div>
    </div>
  );
}
